import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "The Gap",
    lead: "Most companies don't have a marketing problem.",
    body: "A full-time CMO costs $250K+ before they've written a single brief. Agencies execute without owning the outcome. So the founder becomes the marketing department — and growth quietly stalls.",
  },
  {
    n: "02",
    title: "The Model",
    lead: "A CMO-level brain, embedded in your business. None of the overhead.",
    body: "Makkar Growth Partners installs senior marketing leadership inside your company — strategy, team, budget, and accountability — at a fraction of a full-time hire.",
  },
  {
    n: "03",
    title: "The Outcome",
    lead: "Clarity first. Then scale.",
    body: "Repeatable marketing systems, measurable pipeline, and a team that knows exactly what to do next. Most clients see measurable momentum inside the first 30 days.",
  },
];

const Manifesto = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="expertise" data-testid="manifesto-section" className="px-6 md:px-12 py-24 md:py-36">
      <div className="mx-auto max-w-[100rem]">
        <Reveal>
          <p className="overline-label">01 — The Manifesto</p>
        </Reveal>

        <div className="mt-16 md:mt-24">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div
                data-testid={`manifesto-chapter-${c.n}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 border-t border-line py-12 md:py-16"
              >
                <span className="md:col-span-2 font-serif italic text-taupe text-xl">{c.n}.</span>
                <div className="md:col-span-6">
                  <h3 className="font-serif text-3xl md:text-5xl font-light tracking-tight leading-tight">
                    {c.lead}
                  </h3>
                </div>
                <div className="md:col-span-4 flex flex-col justify-end">
                  <p className="text-xs uppercase tracking-[0.25em] text-taupe mb-3">{c.title}</p>
                  <p className="text-sm md:text-base font-light text-ash leading-relaxed">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mt-16 md:mt-28 items-center">
          <Reveal className="md:col-span-5">
            <div ref={imgRef} className="relative overflow-hidden aspect-[4/5]">
              <motion.img
                src="/images/aspen-portrait.webp"
                alt="Aspen Makkar, Founder and Fractional CMO"
                data-testid="founder-portrait"
                style={{ y: imgY }}
                className="absolute inset-0 h-[116%] w-full object-cover object-top"
              />
            </div>
            <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-ash">
              Aspen Makkar — Founder &amp; Fractional CMO
            </p>
          </Reveal>
          <div className="md:col-span-7">
            <Reveal>
              <h3 className="font-serif text-3xl md:text-5xl font-light tracking-tight leading-tight">
                Senior marketing expertise, a founder's mindset, and deep brand
                strategy — <em className="text-taupe">in one seat.</em>
              </h3>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-base md:text-lg font-light text-ash leading-relaxed max-w-xl">
                Seventeen years across brand management, strategic planning,
                content strategy, analytics, website optimization, and team
                leadership — representing premier consumer brands including
                Yerbae, CELSIUS, and Premier Protein through high-converting
                roadshows and experiential activations.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap gap-x-10 gap-y-4">
                {["Yerbae", "CELSIUS", "Premier Protein"].map((b) => (
                  <span key={b} className="font-serif italic text-xl md:text-2xl text-ink/70">
                    {b}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <a
                data-testid="founder-linkedin-link"
                href="https://www.linkedin.com/in/aspenmakkar"
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border-b border-ink/20 hover:border-taupe pb-1 transition-colors duration-300"
              >
                Connect on LinkedIn
                <ArrowUpRight size={14} className="text-taupe transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Manifesto;
